import { useEffect, useRef, useState } from 'react'
import { useLocale } from '../i18n/useLocale'
import { LANGUAGE_OPTIONS as LANGS } from '../i18n/deposit'

// Compact EN/AR dropdown for the site header. Matches the existing nav-dropdown
// material (glassy card, rounded-full trigger, accent-tinted hover). Opens on
// hover (desktop pointer) and click; closes on select, outside-click, Escape.
// A short close delay + a CSS bridge over the trigger→menu gap keep the menu
// open while the cursor travels down to pick a language.
const CLOSE_DELAY_MS = 260

export default function LanguageSwitcher() {
  const { locale, setLocale } = useLocale()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const closeTimer = useRef(null)
  const current = LANGS.find((l) => l.code === locale) || LANGS[0]

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const openNow = () => {
    cancelClose()
    setOpen(true)
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS)
  }

  // Clear any pending timer on unmount.
  useEffect(() => () => cancelClose(), [])

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const choose = (code) => {
    cancelClose()
    setLocale(code)
    setOpen(false)
  }

  return (
    <div
      className={`lang-switcher${open ? ' is-open' : ''}`}
      ref={ref}
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="lang-switcher-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language: ${current.label}. Change language`}
        onClick={() => setOpen((o) => !o)}
      >
        <svg className="lang-globe" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3c2.5 2.5 3.8 5.7 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.7-3.8-9S9.5 5.5 12 3z" />
        </svg>
        <span className="lang-code">{current.code.toUpperCase()}</span>
        <svg className="nav-chevron lang-chevron" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <ul className="lang-switcher-menu" role="listbox" aria-label="Select language">
        {LANGS.map((lang) => (
          <li key={lang.code} role="option" aria-selected={lang.code === locale}>
            <button
              type="button"
              className={`lang-option${lang.code === locale ? ' is-active' : ''}`}
              lang={lang.code}
              dir={lang.code === 'ar' ? 'rtl' : 'ltr'}
              onClick={() => choose(lang.code)}
            >
              <span className="lang-option-native">{lang.native}</span>
              <span className="lang-option-code">{lang.code.toUpperCase()}</span>
              <svg className="lang-check" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
